/* eslint-disable class-methods-use-this */
// Global error handler for non-React errors
class GlobalErrorHandler {
  constructor() {
    this.init();
  }

  init() {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);

    // Handle general JavaScript errors
    window.addEventListener('error', this.handleGlobalError);

    // Handle resource loading errors
    window.addEventListener('error', this.handleResourceError, true);
  }

  handleUnhandledRejection = event => {
    const error = {
      type: 'Unhandled Promise Rejection',
      message: event.reason?.message || event.reason || 'Unknown promise rejection',
      stack: event.reason?.stack || 'No stack trace available',
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    this.showErrorModal(error);
    this.storeError(error);
  };

  handleGlobalError = event => {
    const error = {
      type: 'JavaScript Error',
      message: event.message || 'Unknown error',
      filename: event.filename || 'Unknown file',
      lineno: event.lineno || 'Unknown line',
      colno: event.colno || 'Unknown column',
      stack: event.error?.stack || 'No stack trace available',
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    // Don't show modal for script loading errors or network errors
    if (!event.filename?.includes('extension') && !event.message?.includes('Script error')) {
      this.showErrorModal(error);
    }

    this.storeError(error);
  };

  handleResourceError = event => {
    if (event.target !== window) {
      const error = {
        type: 'Resource Loading Error',
        message: `Failed to load resource: ${event.target.src || event.target.href || 'Unknown resource'}`,
        element: event.target.tagName,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      };

      // Only store, don't show modal for resource errors
      this.storeError(error);
    }
  };

  storeError(error) {
    try {
      const existingErrors = JSON.parse(localStorage.getItem('globalErrors') || '[]');
      existingErrors.push(error);

      // Keep only last 20 errors
      if (existingErrors.length > 20) {
        existingErrors.splice(0, existingErrors.length - 20);
      }

      localStorage.setItem('globalErrors', JSON.stringify(existingErrors));
    } catch {
      // Silently fail if localStorage is not available
    }
  }

  showErrorModal(error) {
    // Check if an error modal is already showing
    if (document.querySelector('.global-error-modal')) {
      return;
    }

    const modal = this.createErrorModal(error);
    document.body.appendChild(modal);
  }

  createErrorModal(error) {
    const modal = document.createElement('div');
    modal.className = 'global-error-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      z-index: 99999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      backdrop-filter: blur(5px);
    `;

    const container = document.createElement('div');
    container.style.cssText = `
      background: white;
      border-radius: 12px;
      max-width: 90vw;
      max-height: 80vh;
      overflow-y: auto;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
      position: relative;
    `;

    const header = document.createElement('div');
    header.style.cssText = `
      background: linear-gradient(135deg, #ef4444, #dc2626);
      color: white;
      padding: 20px;
      border-radius: 12px 12px 0 0;
      display: flex;
      align-items: center;
      gap: 12px;
    `;

    header.innerHTML = `
      <span style="font-size: 24px;">⚠️</span>
      <h2 style="margin: 0; font-size: 18px; font-weight: 600;">Global Error Detected</h2>
    `;

    const closeButton = document.createElement('button');
    closeButton.textContent = '×';
    closeButton.style.cssText = `
      position: absolute;
      top: 15px;
      right: 15px;
      background: rgba(255, 255, 255, 0.2);
      border: none;
      color: white;
      font-size: 20px;
      width: 30px;
      height: 30px;
      border-radius: 50%;
      cursor: pointer;
      transition: background 0.2s;
    `;

    closeButton.onclick = () => {
      document.body.removeChild(modal);
    };

    const content = document.createElement('div');
    content.style.padding = '20px';

    const errorInfo = document.createElement('div');
    errorInfo.style.cssText = `
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      padding: 12px;
      color: #991b1b;
      font-family: 'Courier New', monospace;
      font-size: 14px;
      white-space: pre-wrap;
      word-break: break-all;
      margin-bottom: 16px;
    `;

    errorInfo.textContent = `
Type: ${error.type}
Message: ${error.message}
${error.filename ? `File: ${error.filename}` : ''}
${error.lineno ? `Line: ${error.lineno}:${error.colno}` : ''}
Time: ${error.timestamp}
URL: ${error.url}
    `.trim();

    if (error.stack && error.stack !== 'No stack trace available') {
      const stackTrace = document.createElement('div');
      stackTrace.style.cssText = `
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 12px;
        font-family: 'Courier New', monospace;
        font-size: 12px;
        color: #374151;
        white-space: pre-wrap;
        max-height: 200px;
        overflow-y: auto;
        line-height: 1.4;
        margin-bottom: 16px;
      `;
      stackTrace.textContent = error.stack;
      content.appendChild(stackTrace);
    }

    const buttonGroup = document.createElement('div');
    buttonGroup.style.cssText = `
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      padding-top: 16px;
      border-top: 1px solid #e5e7eb;
    `;

    const copyButton = document.createElement('button');
    copyButton.textContent = '📋 Copy Error Details';
    copyButton.style.cssText = `
      padding: 10px 20px;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      background: white;
      color: #374151;
      border: 1px solid #d1d5db;
    `;

    copyButton.onclick = () => {
      const errorText = `
Global Error Report
Type: ${error.type}
Message: ${error.message}
${error.filename ? `File: ${error.filename}` : ''}
${error.lineno ? `Line: ${error.lineno}:${error.colno}` : ''}
Time: ${error.timestamp}
URL: ${error.url}
User Agent: ${error.userAgent}

Stack Trace:
${error.stack}
      `.trim();

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(errorText);
      }
    };

    const reloadButton = document.createElement('button');
    reloadButton.textContent = '🔄 Reload Page';
    reloadButton.style.cssText = `
      padding: 10px 20px;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      background: #3b82f6;
      color: white;
      border: 1px solid #3b82f6;
    `;

    reloadButton.onclick = () => {
      window.location.reload();
    };

    buttonGroup.appendChild(copyButton);
    buttonGroup.appendChild(reloadButton);

    content.appendChild(errorInfo);
    content.appendChild(buttonGroup);

    container.appendChild(header);
    container.appendChild(closeButton);
    container.appendChild(content);

    modal.appendChild(container);

    // Close modal when clicking outside
    modal.onclick = e => {
      if (e.target === modal) {
        document.body.removeChild(modal);
      }
    };

    return modal;
  }

  destroy() {
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    window.removeEventListener('error', this.handleGlobalError);
    window.removeEventListener('error', this.handleResourceError, true);
  }
}

// Initialize global error handler
const globalErrorHandler = typeof window !== 'undefined' ? new GlobalErrorHandler() : null;

export default globalErrorHandler;
